import React, { useMemo } from 'react';
import {
  MdOutlineBusinessCenter,
  MdOutlineBarChart,
  MdOutlineBiotech,
  MdOutlineArrowForward,
  MdOutlineInventory2,
  MdOutlineHub,
  MdOutlineReplay,
  MdOutlineTimer,
  MdOutlineDateRange,
  MdOutlineSwapHoriz,
  MdOutlineTrendingUp,
} from 'react-icons/md';
import {
  getModelMetricsTotal,
  getModelDateRange,
} from '@process-advisor/process-miner/processMiner/processMiner';

const shell = {
  background: '#F4F5F7',
  minHeight: '100%',
  padding: '12px 18px 18px',
  overflowY: 'auto',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: '#172B4D',
  boxSizing: 'border-box',
};

const card = {
  background: '#FFFFFF',
  border: '1px solid #DFE1E6',
  borderRadius: 14,
  boxShadow: '0 1px 3px rgba(9,30,66,0.08)',
};


const ASSESSMENT_CREDIT_VALID_DAYS = 60;
const ASSESSMENT_CREDIT_VALID_MS = ASSESSMENT_CREDIT_VALID_DAYS * 24 * 60 * 60 * 1000;

const parseAssessmentCreditDate = value => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isFinite(date.getTime()) ? date : null;
};

const getAssessmentCreditAuthorizedAt = (source = {}) => (
  source?.authorizedAt
  || source?.ai_credit_authorized_at
  || source?.assessmentCreditAuthorizedAt
  || source?.createdAt
  || source?.authorization?.createdAt
  || source?.authorization?.authorizedAt
  || source?.authorization?.marker?.createdAt
  || source?.marker?.createdAt
  || source?.assessmentCreditMarker?.createdAt
  || source?.assessmentCreditMarker?.authorizedAt
  || null
);

const getAssessmentCreditExpiresAt = (source = {}) => {
  const explicit = parseAssessmentCreditDate(
    source?.expiresAt
    || source?.validUntil
    || source?.assessmentCreditExpiresAt
    || source?.authorization?.expiresAt
    || source?.authorization?.marker?.expiresAt
    || source?.marker?.expiresAt
    || source?.assessmentCreditMarker?.expiresAt
    || source?.assessmentCreditMarker?.assessmentCreditExpiresAt
  );
  if (explicit) return explicit;
  const authorizedAt = parseAssessmentCreditDate(getAssessmentCreditAuthorizedAt(source));
  return authorizedAt ? new Date(authorizedAt.getTime() + ASSESSMENT_CREDIT_VALID_MS) : null;
};

const getAssessmentCreditDaysRemaining = (source = {}) => {
  const expiresAt = getAssessmentCreditExpiresAt(source?.assessmentCreditMarker || source);
  if (!expiresAt) return null;
  return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
};

const formatAssessmentCreditExpiryDate = (date) => {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  } catch (_) {
    return date.toISOString().slice(0, 10);
  }
};

const isAssessmentCreditUnlocked = (source = {}) => (
  source?.assessmentCreditAuthorized === true
  || source?.hasAssessmentCreditAuthorization === true
  || source?.datasetAiUnlocked === true
  || source?.aiCreditAuthorized === true
);

const numberWithCommas = value => {
  try {
    return new Intl.NumberFormat('en-US').format(value || 0);
  } catch (e) {
    return String(value || 0);
  }
};

const formatDashboardNumber = (num) => {
  const n = Math.round(num ?? 0);
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return n.toLocaleString();
  return n.toString();
};

const formatDashboardPercentage = (num) => Math.round((num ?? 0) * 100) + '%';

const formatDashboardCycleTime = (seconds) => {
  seconds = seconds ?? 0;
  const totalHours = seconds / 3600;
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const hours = Math.floor(totalHours % 24);
    return `${days}d ${hours}h`;
  } else if (totalHours >= 1) {
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
  return `${Math.floor(seconds / 60)}m`;
};

const formatTransitionValue = (val) => {
  const n = Number(val ?? 0);
  if (!Number.isFinite(n)) return '0.0';
  return n.toFixed(1);
};

const normalizeDate = value => {
  if (!value) return null;
  const s = String(value).substring(0, 10).replaceAll('/', '-');
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const buildSnapshot = () => {
  try {
    const total = getModelMetricsTotal?.() || {};
    const dateRange = getModelDateRange?.() || {};
    const from = normalizeDate(dateRange?.from);
    const to = normalizeDate(dateRange?.to);

    return {
      workItems: formatDashboardNumber(total.work_items),
      avgCycleTime: formatDashboardCycleTime(total.avg_cycle_time_started || 0),
      rework: formatDashboardPercentage(total.rework_rate || 0),
      workflowVariants: formatDashboardNumber(total.variants),
      avgWorkItemTransitions: formatTransitionValue(total.avg_transitions),
      flowEfficiency: formatDashboardPercentage(total.avg_flow_efficiency || 0),
      dateRange: from && to ? `${from} to ${to}` : 'No date range',
    };
  } catch (e) {
    return {
      workItems: '0',
      avgCycleTime: '0m',
      rework: '0%',
      workflowVariants: '0',
      avgWorkItemTransitions: '0.0',
      flowEfficiency: '0%',
      dateRange: 'No date range',
    };
  }
};

const Home = ({ appTitle = 'Process Advisor', modelTitle = '', processLabel = 'workflow', entityLabel = 'Work Item', appEdition = 'standard', editionAccess = null, onNavigate = () => {} }) => {
  // Re-compute snapshot whenever the loaded model changes (modelTitle changes when a new
  // model is opened, ensuring the globals getModelMetricsTotal/getModelDateRange are fresh).
  const snapshot = useMemo(() => buildSnapshot(), [modelTitle]);
  const assessmentCreditExpiresAt = getAssessmentCreditExpiresAt(editionAccess?.assessmentCreditMarker || editionAccess || {});
  const assessmentCreditDaysLeft = getAssessmentCreditDaysRemaining(editionAccess || {});
  const assessmentCreditIsUnlocked = isAssessmentCreditUnlocked(editionAccess || {}) && (assessmentCreditDaysLeft === null || assessmentCreditDaysLeft > 0);
  const assessmentCreditExpiryDateText = formatAssessmentCreditExpiryDate(assessmentCreditExpiresAt);
  const sections = [
    {
      id: 'quickHealthcheck',
      title: 'Advisor',
      subtitle: 'Executive assessment and recommendations',
      description: `Use the advisor views for a quick healthcheck, executive assessment, priorities, strategy, and prioritised AI, automation and optimisation opportunities for this ${processLabel.toLowerCase()}.`,
      icon: MdOutlineBusinessCenter,
      bullets: ['Quick healthcheck and process health', 'Trend narrative and execution strategy', 'AI, automation and optimisation opportunities'],
      action: 'Open Advisor',
    },
    {
      id: 'metrics',
      title: 'Evidence',
      subtitle: 'Metrics, variants, transitions, and discoveryRules',
      description: `Review the evidence behind the conclusions so you can validate what is happening in the ${processLabel.toLowerCase()}.`,
      icon: MdOutlineBarChart,
      bullets: ['Metrics and throughput', 'Variants and transitions', 'Structured discoveryRules'],
      action: 'Open Evidence',
    },
    {
      id: 'process_miner',
      title: 'Explorer',
      subtitle: 'Advanced investigation tools',
      description: 'Use the explorer tools when you need root-cause detail, delay analysis, or process exploration.',
      icon: MdOutlineBiotech,
      bullets: ['Process miner and process map', 'Time and delay analysis', 'Clustering and influence analysis'],
      action: 'Open Explorer',
    },
    {
      id: 'valueSimulation',
      title: 'Value',
      subtitle: 'Cost, benefit, and impact modelling',
      description: 'Use Value to estimate the financial and operational impact of workflow improvements.',
      icon: MdOutlineTrendingUp,
      bullets: ['Cost and effort modelling', 'Benefit simulation', 'Savings and value evidence'],
      action: 'Open Value',
    },
    {
      id: 'workbenchFindings',
      title: 'Assessment',
      subtitle: 'Assessment planning and delivery',
      description: 'Use Assessment to turn findings into practical improvement actions, priorities, follow-up work, and the final client report.',
      icon: MdOutlineInventory2,
      bullets: ['Improvement findings', 'Action planning', 'Prioritisation and ownership'],
      action: 'Open Assessment',
    },
  ];

  const snapshotItems = [
    { label: 'Work Items', value: snapshot.workItems, tone: '#E9F2FF', color: '#0C66E4', icon: MdOutlineInventory2 },
    { label: 'Avg Cycle Time', value: snapshot.avgCycleTime, tone: '#EAFBF3', color: '#1F845A', icon: MdOutlineTimer },
    { label: '% Re-work', value: snapshot.rework, tone: '#FCEBEC', color: '#DE350B', icon: MdOutlineReplay },
    { label: 'Process Variants', value: snapshot.workflowVariants, tone: '#FFF4E5', color: '#A05A00', icon: MdOutlineHub },
    { label: 'Avg Work Item Transitions', value: snapshot.avgWorkItemTransitions, tone: '#F2F0FF', color: '#6554C0', icon: MdOutlineSwapHoriz },
    { label: 'Flow Efficiency', value: snapshot.flowEfficiency, tone: '#E8F7F7', color: '#1D7A82', icon: MdOutlineTrendingUp },
  ];

  return (
    <div style={shell}>
      <div style={{ maxWidth: 1380, margin: '0 auto', display: 'grid', gap: 12 }}>
        <section style={{ ...card, padding: '12px 14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '0.82fr 1.18fr', gap: 12, alignItems: 'stretch' }}>
            <div style={{ paddingRight: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0C66E4', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Home
              </div>
              <h1 style={{ margin: '6px 0 8px 0', fontSize: 23, lineHeight: 1.12, letterSpacing: '-0.02em', fontWeight: 700 }}>
                Welcome to {appTitle}
              </h1>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.42, color: '#42526E' }}>
                Use <strong>Advisor</strong> for the executive assessment and recommended actions, <strong>Evidence</strong> to validate the underlying metrics and patterns,
                <strong>Explorer</strong> for root-cause investigation, <strong>Value</strong> to estimate impact, and <strong>Assessment</strong> to plan improvements and generate the final report.
              </p>

              {assessmentCreditIsUnlocked ? (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 10, border: '1px solid #ABF5D1', background: '#E3FCEF', color: '#164B35', fontSize: 12.5, lineHeight: 1.35, fontWeight: 600 }}>
                  Assessment Credit active: <strong>{assessmentCreditDaysLeft ?? ASSESSMENT_CREDIT_VALID_DAYS} day{(assessmentCreditDaysLeft ?? ASSESSMENT_CREDIT_VALID_DAYS) === 1 ? '' : 's'} left</strong>{assessmentCreditExpiryDateText ? <> — valid until {assessmentCreditExpiryDateText}</> : null}. Full model and AI Advisor outputs remain unlocked until then.
                </div>
              ) : null}

              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <PrimaryAction onClick={() => onNavigate('quickHealthcheck')} icon={MdOutlineBusinessCenter} label="Start with Advisor" />
                <SecondaryAction onClick={() => onNavigate('metrics')} icon={MdOutlineBarChart} label="View Evidence" />
                <SecondaryAction onClick={() => onNavigate('process_miner')} icon={MdOutlineBiotech} label="Open Explorer" />
              </div>
            </div>

            <div style={{ ...card, padding: 12, background: '#FCFDFE' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#172B4D' }}>Process snapshot</div>
                  {modelTitle ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: '#E9F2FF', color: '#0C66E4', fontSize: 12, fontWeight: 700, maxWidth: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{modelTitle}</span>
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#F4F5F7', color: '#5E6C84', fontSize: 12, border: '1px solid #DFE1E6', flexShrink: 0 }}>
                  <MdOutlineDateRange size={15} />
                  {snapshot.dateRange}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7 }}>
                {snapshotItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      style={{
                        minHeight: 48,
                        padding: '8px 10px',
                        borderRadius: 10,
                        background: item.tone,
                        border: '1px solid rgba(9,30,66,0.05)',
                        display: 'grid',
                        gridTemplateColumns: '24px 1fr',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ width: 24, height: 24, borderRadius: 7, background: '#FFFFFF', color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={13} />
                      </div>
                      <div>
                        <div style={{ fontSize: 9.5, color: item.color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{item.label}</div>
                        <div style={{ marginTop: 2, fontSize: 12.5, lineHeight: 1.1, fontWeight: 800, color: '#172B4D' }}>{item.value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          {sections.map(section => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => onNavigate(section.id)}
                style={{
                  ...card,
                  padding: '14px 16px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                  background: '#FFFFFF',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#E9F2FF', color: '#0C66E4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} />
                  </div>
                  <div style={{ color: '#6B778C', paddingTop: 2 }}>
                    <MdOutlineArrowForward size={18} />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 18, lineHeight: 1.15, fontWeight: 700, color: '#172B4D' }}>{section.title}</div>
                  <div style={{ marginTop: 4, fontSize: 12.5, color: '#0C66E4', fontWeight: 500 }}>{section.subtitle}</div>
                </div>

                <div style={{ fontSize: 13.5, lineHeight: 1.4, color: '#42526E' }}>{section.description}</div>

                <ul style={{ margin: 0, paddingLeft: 16, display: 'grid', gap: 4, color: '#42526E', fontSize: 12.5, lineHeight: 1.35 }}>
                  {section.bullets.map(item => <li key={item}>{item}</li>)}
                </ul>

                <div style={{ marginTop: 'auto', color: '#0C66E4', fontWeight: 700, fontSize: 13 }}>{section.action}</div>
              </button>
            );
          })}
        </section>
      </div>
    </div>
  );
};

const PrimaryAction = ({ onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    style={{
      height: 38,
      padding: '0 14px',
      borderRadius: 10,
      border: '1px solid #0C66E4',
      background: '#0C66E4',
      color: '#FFFFFF',
      fontSize: 13,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(12,102,228,0.18)',
      whiteSpace: 'nowrap',
    }}
  >
    <Icon size={16} />
    {label}
  </button>
);

const SecondaryAction = ({ onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    style={{
      height: 38,
      padding: '0 14px',
      borderRadius: 10,
      border: '1px solid #B3D4FF',
      background: '#FFFFFF',
      color: '#172B4D',
      fontSize: 13,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    <Icon size={16} />
    {label}
  </button>
);

export default Home;
